import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Button,
} from '@mui/material';
import AndroidIcon from '@mui/icons-material/Android';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import LanguageIcon from '@mui/icons-material/Language';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { PageIndices } from '../../common/i-doc/i-doc.registry.ts';
import { DashboardLayout } from '@components/DashboardLayout.tsx';
import { PageCard } from '@components/common/PageCard.tsx';
import { UserAvatar } from '@components/common/UserAvatar.tsx';
import { PageHint } from '@components/common/PageHint.tsx';
import { PageToolbar } from '@components/common/PageToolbar.tsx';
import { UniversalPagination } from '@components/common/UniversalPagination.tsx';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog.tsx';
import { adminDevicesService, AdminDeviceToken } from '@services/admin-devices.service.ts';
import { toastService } from '@services/toast.service.ts';

const toLocalDateStr = (d: Date) =>
  d.toLocaleDateString('uk-UA', { timeZone: userTimeZone });

const isSameDay = (d1: Date, d2: Date) =>
  toLocalDateStr(d1) === toLocalDateStr(d2);

const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const formatDate = (iso: string): string => {
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  const time = date.toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit', timeZone: userTimeZone });

  if (isSameDay(date, today)) return `Сьогодні, ${time}`;
  if (isSameDay(date, yesterday)) return `Вчора, ${time}`;
  return date.toLocaleDateString('uk-UA', { day: 'numeric', month: 'short', year: 'numeric', timeZone: userTimeZone }) + `, ${time}`;
};

const PLATFORM_OPTIONS = [
  { value: '', label: 'Всі платформи' },
  { value: 'web', label: 'Web' },
  { value: 'android', label: 'Android' },
  { value: 'ios', label: 'iOS' },
];

const PlatformChip: React.FC<{ platform: 'android' | 'ios' | 'web' }> = ({ platform }) => {
  const map = {
    android: { icon: <AndroidIcon />,    label: 'Android', color: 'success' as const },
    ios:     { icon: <PhoneIphoneIcon />, label: 'iOS',     color: 'default' as const },
    web:     { icon: <LanguageIcon />,    label: 'Web',     color: 'primary' as const },
  };
  const m = map[platform];
  return <Chip size="small" icon={m.icon} label={m.label} color={m.color} variant="outlined" />;
};

const AdminDevicesPageContent: React.FC = () => {
  const [search, setSearch] = useState('');
  const [platform, setPlatform] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const [devices, setDevices] = useState<AdminDeviceToken[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [stats, setStats] = useState<{ web: number; android: number; ios: number } | null>(null);

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [testing, setTesting] = useState<number | null>(null);
  const [testResults, setTestResults] = useState<Record<number, { success: boolean; platform?: string; error?: string }>>({});

  // Popover для кастомного тексту тесту
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [popoverDeviceId, setPopoverDeviceId] = useState<number | null>(null);
  const [testTitle, setTestTitle] = useState('');
  const [testBody, setTestBody] = useState('');
  const popoverOpen = Boolean(popoverAnchor);

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminDevicesService.getDevices({
        page,
        limit,
        search: search || undefined,
        platform: (platform as 'android' | 'ios' | 'web') || undefined,
      });
      setDevices(res.data);
      setTotal(res.meta.total);
    } catch {
      toastService.error('Не вдалося завантажити список токенів.');
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, platform]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  useEffect(() => {
    Promise.all([
      adminDevicesService.getDevices({ platform: 'web',     limit: 1 }),
      adminDevicesService.getDevices({ platform: 'android', limit: 1 }),
      adminDevicesService.getDevices({ platform: 'ios',     limit: 1 }),
    ]).then(([web, android, ios]) => {
      setStats({ web: web.meta.total, android: android.meta.total, ios: ios.meta.total });
    }).catch(() => {});
  }, []);

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await adminDevicesService.deleteDevice(deleteId);
      toastService.success('Токен видалено.');
      setDevices((prev) => prev.filter((d) => d.id !== deleteId));
      setTotal((prev) => prev - 1);
    } catch {
      toastService.error('Не вдалося видалити токен.');
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  const handleTestPush = async (id: number, title?: string, body?: string) => {
    setPopoverAnchor(null);
    setPopoverDeviceId(null);
    setTesting(id);
    try {
      const result = await adminDevicesService.testPush(id, title || undefined, body || undefined);
      setTestResults((prev) => ({ ...prev, [id]: result }));
    } catch (error: any) {
      const errMsg = error.response?.data?.data?.error ?? error.response?.data?.message ?? error.message;
      setTestResults((prev) => ({ ...prev, [id]: { success: false, error: errMsg } }));
    } finally {
      setTesting(null);
    }
  };

  const deviceToDelete = devices.find((d) => d.id === deleteId);

  return (
    <Box data-i-doc={PageIndices.ADMIN_DEVICES}>
      <PageHint iDoc={PageIndices.ADMIN_DEVICES} />

      {stats && (
        <Paper variant="outlined" sx={{ mb: 2, p: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Всього токенів:</Typography>
          <Chip size="small" icon={<LanguageIcon />}    label={`Web: ${stats.web}`}         color="primary" />
          <Chip size="small" icon={<AndroidIcon />}     label={`Android: ${stats.android}`} color="success" />
          <Chip size="small" icon={<PhoneIphoneIcon />} label={`iOS: ${stats.ios}`} />
          <Chip size="small" label={`Всього: ${stats.web + stats.android + stats.ios}`} variant="outlined" />
        </Paper>
      )}

      <PageCard>
        <PageToolbar
          storageKey="filters:admin-devices"
          filters={[
            {
              id: 'search',
              type: 'text',
              label: 'Пошук за іменем або email',
              value: search,
              onChange: (v) => { setSearch(v); setPage(1); },
              flexGrow: 1,
            },
            {
              id: 'platform',
              type: 'select',
              label: 'Платформа',
              value: platform,
              onChange: (v) => { setPlatform(v); setPage(1); },
              options: PLATFORM_OPTIONS,
            },
          ]}
          onReset={() => { setSearch(''); setPlatform(''); setPage(1); }}
          showReset
        />

        {loading ? (
          <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : devices.length === 0 ? (
          <Typography sx={{ mt: 4, textAlign: 'center' }}>Токенів не знайдено.</Typography>
        ) : (
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Користувач</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Платформа</TableCell>
                  <TableCell>Токен</TableCell>
                  <TableCell>Device ID</TableCell>
                  <TableCell>Оновлено</TableCell>
                  <TableCell>Зареєстровано</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {devices.map((device) => (
                  <TableRow key={device.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <UserAvatar path={device.user.avatar} name={device.user.name} sx={{ width: 28, height: 28, fontSize: '0.75rem' }} />
                        <Typography variant="body2">
                          {[device.user.name, device.user.surname].filter(Boolean).join(' ')}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{device.user.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <PlatformChip platform={device.platform} />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={device.token}>
                        <Typography
                          variant="caption"
                          sx={{ fontFamily: 'monospace', maxWidth: 200, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {device.token}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }} color={device.deviceId ? 'text.primary' : 'text.disabled'}>
                        {device.deviceId ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(device.updatedAt)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(device.createdAt)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip title="Надіслати тестовий push">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={testing === device.id}
                              onClick={(e) => {
                                setPopoverAnchor(e.currentTarget);
                                setPopoverDeviceId(device.id);
                                setTestTitle('');
                                setTestBody('');
                              }}
                            >
                              {testing === device.id
                                ? <CircularProgress size={16} />
                                : <SendIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>

                        {testResults[device.id] && (
                          testResults[device.id].success ? (
                            <Tooltip title={`Доставлено (${testResults[device.id].platform})`}>
                              <CheckCircleIcon fontSize="small" color="success" />
                            </Tooltip>
                          ) : (
                            <Tooltip title={testResults[device.id].error ?? 'Помилка'}>
                              <ErrorIcon fontSize="small" color="error" />
                            </Tooltip>
                          )
                        )}

                        <Tooltip title="Видалити токен">
                          <IconButton size="small" color="error" onClick={() => setDeleteId(device.id)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <UniversalPagination
          page={page}
          count={Math.ceil(total / limit)}
          limit={limit}
          total={total}
          onPageChange={setPage}
          onLimitChange={(v) => { setLimit(v); setPage(1); }}
          storageKey="pagination:admin-devices"
        />
      </PageCard>

      <Popover
        open={popoverOpen}
        anchorEl={popoverAnchor}
        onClose={() => { setPopoverAnchor(null); setPopoverDeviceId(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, width: 280 }}>
          <Typography variant="subtitle2">Тестовий push</Typography>
          <TextField
            label="Title (опціонально)"
            value={testTitle}
            onChange={(e) => setTestTitle(e.target.value)}
            placeholder="Дефолтний заголовок"
            fullWidth
          />
          <TextField
            label="Body (опціонально)"
            value={testBody}
            onChange={(e) => setTestBody(e.target.value)}
            placeholder="Дефолтний текст"
            fullWidth
            multiline
            rows={2}
          />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={() => { setPopoverAnchor(null); setPopoverDeviceId(null); }}>
              Скасувати
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<SendIcon />}
              onClick={() => popoverDeviceId && handleTestPush(popoverDeviceId, testTitle, testBody)}
            >
              Надіслати
            </Button>
          </Box>
        </Box>
      </Popover>

      <ConfirmationDialog
        open={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDeleteConfirm}
        title="Видалити токен"
        message={`Видалити ${deviceToDelete?.platform ?? ''}-токен користувача ${[deviceToDelete?.user.name, deviceToDelete?.user.surname].filter(Boolean).join(' ') ?? ''}? Пристрій перестане отримувати push-сповіщення до наступної реєстрації.`}
        confirmText={deleting ? 'Видалення...' : 'Видалити'}
        cancelText="Скасувати"
      />
    </Box>
  );
};

export const AdminDevicesPage: React.FC = () => (
  <DashboardLayout>
    <AdminDevicesPageContent />
  </DashboardLayout>
);
